import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Hard subscription gate. Wraps the authenticated app.
 * - Always allows /app/settings/* (so users can pay / manage billing)
 * - Blocks every other /app/* route when access.active === false
 * - Users KEEP READ access to their data via Supabase — this gate is UX-level
 *   and the DB-level RLS RESTRICTIVE policies in migration 0013 block WRITES
 */
export default function PaywallGuard({ children }) {
  const { currentOrgId, currentRole } = useAuth();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const check = async () => {
    if (!currentOrgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await apiFetch(`/access/status?organization_id=${currentOrgId}`);
      setStatus(r);
    } catch (_) {
      setStatus({ active: true }); // fail-open on transient errors — don't lock users out
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, [currentOrgId]);
  // Re-check when the user navigates back from a Stripe redirect
  useEffect(() => {
    if (pathname.includes("settings/billing") && window.location.search.includes("checkout=success")) {
      setTimeout(check, 1500); // give webhook a moment
    }
    // eslint-disable-next-line
  }, [pathname]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#57534E]">Loading your workspace…</div>;
  }

  // Always allow settings + billing + subscription pages (so admins can pay)
  const isAllowed = /^\/app\/settings/.test(pathname) || pathname === "/app/settings" || pathname.startsWith("/app/subscription-required");
  if (!status || status.active) return children;
  if (isAllowed) return children;

  return <BlockedScreen status={status} role={currentRole} onRecheck={() => { setRefreshing(true); check(); }} refreshing={refreshing} />;
}

function BlockedScreen({ status, role, onRecheck, refreshing }) {
  const reason = status?.reason || "no_subscription";
  const isAdmin = role === "org_admin";
  const messages = {
    trial_expired: {
      title: "Your free trial has ended",
      body: "Add a payment method to restore access. Your clients, forms, and past data are safe and will reappear once billing is active.",
    },
    cancelled: {
      title: "Your subscription is canceled",
      body: "Reactivate to restore full access. All your data is safely preserved.",
    },
    unpaid: {
      title: "Payment failed",
      body: "Your last payment didn't go through. Update your card to unlock the workspace.",
    },
    payment_failed: {
      title: "Payment failed",
      body: "Your last payment didn't go through. Update your card to unlock the workspace.",
    },
    expired: {
      title: "Subscription expired",
      body: "Choose a plan to unlock the workspace. Your data is safely preserved.",
    },
    suspended: {
      title: "Subscription paused",
      body: "Reactivate your subscription to continue.",
    },
    no_subscription: {
      title: "Choose a plan",
      body: "Pick a plan to activate ClientDesk VA for your team.",
    },
  };
  const m = messages[reason] || messages.no_subscription;

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="paywall-block-screen">
      <div className="max-w-lg w-full bg-white rounded-3xl border border-[#E8E2D9] p-8 md:p-10 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-[#FDDCDC] flex items-center justify-center mb-5">
          <Lock size={22} className="text-[#B02222]" strokeWidth={1.75} />
        </div>
        <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-2">{m.title}</h1>
        <p className="text-sm text-[#57534E] leading-relaxed mb-6">{m.body}</p>

        <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E2D9] p-4 mb-6 text-xs text-[#57534E] space-y-1">
          <div className="flex justify-between"><span>Status</span><b className="text-[#1C1917] capitalize">{status?.status || "—"}</b></div>
          {status?.trial_ends_at && <div className="flex justify-between"><span>Trial ended</span><b className="text-[#1C1917]">{new Date(status.trial_ends_at).toLocaleDateString()}</b></div>}
          {status?.current_period_end && <div className="flex justify-between"><span>Period ended</span><b className="text-[#1C1917]">{new Date(status.current_period_end).toLocaleDateString()}</b></div>}
          <div className="flex justify-between"><span>Access</span><b className="text-[#B02222]">Blocked</b></div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {isAdmin ? (
            <Link to="/app/settings/billing" className="flex-1">
              <Button data-testid="paywall-block-upgrade" className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                Upgrade / manage billing
              </Button>
            </Link>
          ) : (
            <div className="flex-1 text-xs text-[#57534E] bg-[#FDE9D8] p-3 rounded-lg">
              Ask your organization owner to reactivate the subscription. You'll regain access automatically once billing is restored.
            </div>
          )}
          <Button data-testid="paywall-block-recheck" onClick={onRecheck} variant="outline" className="rounded-full" disabled={refreshing}>
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Checking…" : "I've paid, recheck"}
          </Button>
        </div>
        <p className="text-xs text-[#A8A29E] mt-6">Your clients, appointments, tasks, forms and documents remain safely stored. You'll see them again the moment your subscription is active.</p>
      </div>
    </div>
  );
}
